// schema
// actions
// util
import { getEnvironment } from "@clockwise/client-commons/src/config/environment";
import { addAsanaLink, addAsanaLinkDev } from "@clockwise/client-commons/src/constants/site";
// components
import { ErrorScreen } from "@clockwise/web-commons/src/components/error-screen";
import { LoaderClock } from "@clockwise/web-commons/src/components/loader-clock";
// assets and styles
import { colors, greys } from "@clockwise/web-commons/src/styles/color.styles";
import { fontFamily, fontFamilySans } from "@clockwise/web-commons/src/styles/type.styles";
import { CwLink } from "@clockwise/web-commons/src/ui/cw-link";
// state and mutations
import { aclackaTasksInFocusTime } from "#webapp/src/assets/img/illustrations";
import { OnboardingStep } from "@clockwise/web-commons/src/util/onboarding.util";
import { getServerUrl } from "@clockwise/web-commons/src/util/post-message-common.util";
// material-ui
import * as ISchema from "#webapp/src/__schema__";
import { saveAsanaCredentials } from "#webapp/src/mutations";
import { asanaAuthReferrer, asanaUserLevelNonce } from "#webapp/src/state/local-storage";
import { getOnboardingFlowState } from "#webapp/src/state/onboarding";
import { TrackingEvents, asanaTrack as track } from "#webapp/src/util/analytics.util";
import { logger } from "#webapp/src/util/logger.util";
import { getCurrentOrg } from "#webapp/src/util/org.util";
import {
  generateBaseAsanaAuthUrl,
  getAsanaRedirectUri,
} from "#webapp/src/util/third-party-oauth.util";
import { Link, Typography } from "@clockwise/design-system";
import { appPaths } from "@clockwise/web-commons/src/constants/route.constants";
import { makeStyles } from "@material-ui/core";
// libraries
import { EcosystemEnum } from "@clockwise/schema";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import { CircularProgress } from "@material-ui/core";
import { useRouter } from "found";
import * as React from "react";
import { RelayProp } from "react-relay";

export interface AsanaAuthProps {
  viewer: ISchema.IViewer;
  relay: RelayProp;
}

type IAsanaAuthStep =
  | "kickoff-auth"
  | "onboarding"
  | "existing-credentials-present"
  | "saving-credentials"
  | "processing-redirect-success"
  | "processing-redirect-failure"
  | "user-denied-auth";

const useStyles = makeStyles({
  successContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  successImage: {
    display: "block",
    maxWidth: 350,
    width: "auto",
    height: "auto",
    margin: "60px 20px 34px",
  },
  successTextHeader: {
    fontFamily,
    fontSize: 22,
    color: colors.blueDusk.darker,
    textAlign: "center",
  },
  successTextSubtitle: {
    fontFamily: fontFamilySans,
    fontSize: 16,
    color: greys.slate,
    textAlign: "center",
  },
  manage: {
    paddingTop: 16,
  },
});

export const AsanaAuth = ({ viewer, relay }: AsanaAuthProps) => {
  const classes = useStyles();
  const ecosystem = useEcosystem();
  const { router } = useRouter();

  const [step, setStep] = React.useState<IAsanaAuthStep>("kickoff-auth");

  const org = getCurrentOrg(viewer);
  const onboardingState = viewer && getOnboardingFlowState(viewer.user);

  React.useEffect(() => {
    if (!viewer || !org) {
      return;
    }

    const currentOnboardingState = onboardingState?.current?.state;
    if (currentOnboardingState !== OnboardingStep.Finished) {
      if (currentOnboardingState === OnboardingStep.Unstarted) {
        track(TrackingEvents.AUTH.ASANA.STARTED_ONBOARDING_FROM_ASANA);
        sessionStorage.setItem("onboardingExternalAppSource", "asana");
      }
    }
    /**
     * At the moment, if a user is not logged in, the onboarding will not work,
     * as a lot of the onboarding depends on redux states that asanaAuth is not linked to.
     * Commenting out vs removing in case we missed something
     */
    // if (currentOnboardingState !== OnboardingStep.Finished) {
    //   setStep("onboarding");
    //   if (currentOnboardingState === OnboardingStep.Unstarted) {
    //     track(TrackingEvents.AUTH.ASANA.STARTED_ONBOARDING_FROM_ASANA);
    //   }
    //   return;
    // } else {
    //   if (step === "onboarding") {
    //     track(TrackingEvents.AUTH.ASANA.FINISHED_ONBOARDING_FROM_ASANA);
    //   }
    // }

    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.has("error")) {
      if (searchParams.get("error_description") === "The user denied the authorization request.") {
        setStep("user-denied-auth");
        track(TrackingEvents.AUTH.ASANA.DENIED_ASANA);
      } else {
        setStep("processing-redirect-failure");
        track(TrackingEvents.AUTH.ASANA.ENCOUNTERED_ERROR_ASANA);
      }
      asanaUserLevelNonce.remove();
      return;
    }

    // Check if query params don't exist, indicating to start the oauth.
    if (!searchParams.has("code") || !searchParams.has("state")) {
      track(TrackingEvents.AUTH.ASANA.ARRIVED_ASANA_AUTH);

      // Store the window referrer to know where to send users after they auth.
      asanaAuthReferrer.set(document.referrer);
      // Kickoff the Oath by redirecting to generated asanaAuthUrl.
      const asanaAuthUrl = generateBaseAsanaAuthUrl();
      window.location.href = asanaAuthUrl;
      return;
    } else {
      // We have both code and state in the search params. Lets see if things match up.
      const state = searchParams.get("state");

      const storedNonce = asanaUserLevelNonce.get();
      asanaUserLevelNonce.remove();
      if (!storedNonce || storedNonce !== state) {
        logger.error(
          "Unable to complete Asana authentication. State parameter does not match stored state parameter, indicating a possible intrusion.",
          { expected: state, actual: storedNonce ? "null" : storedNonce },
        );
        // setStep("processing-redirect-failure");
        track(TrackingEvents.AUTH.ASANA.ENCOUNTERED_ERROR_ASANA);
      }
      track(TrackingEvents.AUTH.ASANA.AUTHENTICATED_ASANA);
      setStep("saving-credentials");
    }

    const code = searchParams.get("code");
    if (org && relay && code) {
      // Send code back to server!
      saveAsanaCredentials(
        relay.environment,
        { orgRelayId: org.id, code, redirectUri: getAsanaRedirectUri() },
        () => {
          setStep("processing-redirect-success");
          if (asanaAuthReferrer?.get()?.includes("app.asana.com")) {
            // User was sent to /asana from Asana, so postMessage back to
            // Asana to indicate that they finished oauth.
            window?.opener?.postMessage("success", "https://app.asana.com");
          } else {
            // User navigated to /asana from CW Settings or elsewhere. Send to
            // Asana integration landing page in Asana.
            if (getEnvironment() === "production") {
              window.open(addAsanaLink, "_self");
              window?.opener?.location?.reload(); // Needed to change button from remove Asana to add Asana
            } else if (getEnvironment() === "development") {
              window.open(addAsanaLinkDev, "_self");
              window?.opener?.location?.reload(); // Needed to change button from remove Asana to add Asana
            }
          }
          router.replace(appPaths.onboarding);
        },
        () => {
          logger.error(
            "Unable to complete Asana authentication. Unable to save authentication code and redirect uri.",
          );
          setStep("processing-redirect-failure");
          track(TrackingEvents.AUTH.ASANA.ENCOUNTERED_ERROR_ASANA);
        },
      );
    } else {
      logger.error("Unable to complete Asana authentication. Missing org, relay, or auth code.");
      setStep("processing-redirect-failure");
      track(TrackingEvents.AUTH.ASANA.ENCOUNTERED_ERROR_ASANA);
    }
  }, [viewer]);

  if (!onboardingState || !viewer || !org) {
    return null;
  }

  if (onboardingState.current.state !== OnboardingStep.Finished) {
    return (
      <div className="cw-flex cw-items-center cw-justify-center cw-h-full">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      {step === "existing-credentials-present" && (
        <div>
          <div className={classes.successContainer}>
            <img className={classes.successImage} src={aclackaTasksInFocusTime} />
            <Typography className={classes.successTextHeader}>You're all set! 🎉</Typography>
            <Typography className={classes.successTextSubtitle}>
              {document.referrer?.includes("app.asana.com")
                ? "You have already granted Clockwise access. You can close this page and return to Asana."
                : "You have already granted Clockwise access to Asana."}
            </Typography>
            <CwLink
              className={classes.manage}
              size={14}
              onClick={() => {
                if (ecosystem === EcosystemEnum.Microsoft) {
                  window.open(`${getServerUrl()}/app/web-settings/integrations/asana`, "_self");
                } else {
                  window.open(`${getServerUrl()}/app/settings/asana`, "_self");
                }
              }}
            >
              Manage your Clockwise integrations
            </CwLink>
          </div>
        </div>
      )}
      {step === "saving-credentials" && (
        <div>
          <LoaderClock />
        </div>
      )}
      {step === "processing-redirect-success" && (
        <div className={classes.successContainer}>
          <img className={classes.successImage} src={aclackaTasksInFocusTime} />
          <Typography className={classes.successTextHeader}>You're all set! 🎉</Typography>
          <Typography className={classes.successTextSubtitle}>
            You can close this page and return to Asana
          </Typography>
        </div>
      )}
      {step === "processing-redirect-failure" && (
        <div>
          <ErrorScreen title="Couldn't connect Asana to Clockwise">
            To give it another go, close this window and try again from Asana.
            <br />
            <br />
            If the issue persists, contact us at{" "}
            <Link href="mailto:support@getclockwise.com">support@getclockwise.com</Link>.
          </ErrorScreen>
        </div>
      )}
      {step === "user-denied-auth" && (
        <div>
          <ErrorScreen title="Couldn't connect Asana to Clockwise">
            Looks like you denied Clockwise permission to access Asana.
            <br />
            <br />
            For any questions, contact us at{" "}
            <Link href="mailto:support@getclockwise.com">support@getclockwise.com</Link>.
          </ErrorScreen>
        </div>
      )}
    </div>
  );
};
